import React, { useMemo, useState, useEffect, useRef } from 'react';

import { Icon } from '@iconify/react';
import { HELPERS } from '@/utils';
import ReactPixel from 'react-facebook-pixel';
import { sendCustomTag } from '@/utils/clarity-tags';
import { setBannerVisibility } from '@/hooks/useBannerVisibility';

const StickyBanner = ({ 
  bannerData = null,
  title = "Test Banner", 
  content = "", 
  buttonText = "", 
  buttonLink = "#",
  backgroundColor = "bg-gradient-to-r from-purple-600 to-blue-600",
  textColor = "text-white",
  titleColor = "text-white",
  buttonColor = "bg-white hover:bg-gray-100",
  buttonTextColor = "text-purple-600",
  borderColor = "border-purple-500",
  logoSrc = "",
  logoAlt = "",
  showLogo = true,
  showButton = true,
  showCloseButton = true,
  isVisible = true,
  onClose = null
}) => {
  const [visible, setVisible] = useState(isVisible);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const bannerRef = useRef(null);
  
  // Use API data if available, fallback to props
  const displayData = useMemo(() => bannerData ? {
    title: bannerData.title || title,
    content: bannerData.content || content,
    buttonText: bannerData.button_text || buttonText,
    buttonLink: bannerData.button_link || buttonLink,
    backgroundColor: bannerData.background_color || backgroundColor,
    textColor: bannerData.text_color || textColor,
    titleColor: bannerData.title_color || titleColor,
    buttonColor: bannerData.button_color || buttonColor,
    buttonTextColor: bannerData.button_text_color || buttonTextColor,
    borderColor: bannerData.border_color || borderColor,
    logoSrc: bannerData.logo_src || logoSrc,
    logoAlt: bannerData.logo_alt || logoAlt,
    showLogo: bannerData.show_logo !== undefined ? bannerData.show_logo : showLogo,
    showButton: bannerData.show_button !== undefined ? bannerData.show_button : showButton,
    showCloseButton: bannerData.show_close_button !== undefined ? bannerData.show_close_button : showCloseButton,
  } : {
    title, content, buttonText, buttonLink, backgroundColor, textColor, 
    titleColor, buttonColor, buttonTextColor, borderColor, logoSrc, logoAlt,
    showLogo, showButton, showCloseButton
  }, [bannerData, title, content, buttonText, buttonLink, backgroundColor, textColor, titleColor, buttonColor, buttonTextColor, borderColor, logoSrc, logoAlt, showLogo, showButton, showCloseButton]);
  
  // Track banner view and update global visibility
  useEffect(() => {
    if (visible) {
      // Simple height check after DOM render
      const checkHeight = () => {
        if (bannerRef.current) {
          const height = bannerRef.current.offsetHeight;
          setBannerVisibility(true, height);
        }
      };
      
      // Check height after a short delay to ensure render is complete
      setTimeout(checkHeight, 100);
      
      if (!hasTrackedView) {
        HELPERS.sendEventGA('banner_viewed', {
          banner_type: 'sticky_banner',
          banner_title: displayData.title,
          banner_content: displayData.content,
          page_path: window.location.pathname,
          has_button: displayData.showButton,
          button_text: displayData.buttonText
        });

        ReactPixel.track('ViewContent', {
          content_name: 'Sticky Banner',
          content_category: 'banner',
          banner_title: displayData.title
        });

        sendCustomTag('banner', 'viewed');
        setHasTrackedView(true);
      }
    } else {
      setBannerVisibility(false);
    }
    
    return () => {
      if (visible) {
        setBannerVisibility(false);
      }
    };
  }, [visible, hasTrackedView, displayData]);
  
  if (!visible) return null;
  
  const handleClose = () => {
    // Track banner dismiss
    HELPERS.sendEventGA('banner_dismissed', {
      banner_type: 'sticky_banner',
      banner_title: displayData.title,
      banner_content: displayData.content,
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerDismissed',
      banner_type: 'sticky_banner',
      banner_title: displayData.title
    });

    sendCustomTag('banner', 'dismissed');
    
    setVisible(false);
    if (onClose) {
      onClose();
    }
  };
  
  const handleBannerClick = () => {
    // Track banner click
    HELPERS.sendEventGA('banner_clicked', {
      banner_type: 'sticky_banner',
      banner_title: displayData.title,
      button_text: displayData.buttonText,
      destination_link: displayData.buttonLink,
      page_path: window.location.pathname
    });

    ReactPixel.track('CustomEvent', {
      event_name: 'BannerClicked',
      banner_type: 'sticky_banner',
      destination: displayData.buttonLink
    });

    sendCustomTag('banner', 'clicked');
  };
  
  // Banner always stays at the top
  const positionClasses = "fixed top-0 left-0 right-0 z-[60]";
  const sizeClasses = "w-full";
  
  return (
    <div 
      ref={bannerRef}
      className={`${positionClasses} ${sizeClasses} p-2 lg:p-4 border`}
      style={{ 
        minHeight: '48px',
        backgroundColor: displayData.backgroundColor || '#FAF9FB',
        color: displayData.textColor || '#231838',
        borderColor: displayData.borderColor || '#e3e2e3'
      }}
    >
      {/* Mobile Layout - Compact single line */}
      <div className="flex items-center justify-between lg:hidden">
        <div className="flex items-center flex-1 gap-2">
          {displayData.showLogo && displayData.logoSrc && (
            <>
              {displayData.logoSrc.startsWith('http') || displayData.logoSrc.startsWith('/') ? (
                <img src={displayData.logoSrc} className="h-3 w-auto flex-shrink-0" alt={displayData.logoAlt} />
              ) : (
                <Icon icon={displayData.logoSrc} className="h-3 w-3 flex-shrink-0" />
              )}
            </>
          )}
          <div className="flex-1 min-w-0">
            {displayData.title && (
              <span className="font-medium text-xs line-clamp-1" style={{ color: displayData.titleColor || '#231838' }}>
                {displayData.title}
              </span>
            )}
            {displayData.content && (
              <p className="text-xs leading-tight line-clamp-1" style={{ color: displayData.textColor || '#231838' }}>
                {displayData.content}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          {displayData.showButton && displayData.buttonText && (
            <a
              href={displayData.buttonLink}
              onClick={handleBannerClick}
              className="px-2 py-1 text-xs font-medium rounded text-nowrap transition-colors duration-200"
              style={{ 
                color: displayData.buttonTextColor || '#FFFFFF',
                backgroundColor: displayData.buttonColor || '#926fe5'
              }}
            >
              {displayData.buttonText}
            </a>
          )}
          
          {displayData.showCloseButton && (
            <button
              onClick={handleClose}
              type="button"
              className="inline-flex justify-center w-4 h-4 items-center hover:opacity-75 rounded p-0.5 transition-colors duration-200 ml-1"
              style={{ color: displayData.textColor || '#231838' }}
              aria-label="Close banner"
            >
              <Icon icon="material-symbols:close" className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex lg:flex-row lg:justify-center lg:items-center lg:space-x-4 relative">
        {/* Centered Content */}
        <div className="flex items-center space-x-4">
          {(displayData.showLogo && (displayData.logoSrc || displayData.title)) && (
            <div className="flex items-center space-x-3">
              {displayData.showLogo && displayData.logoSrc && (
                <>
                  {displayData.logoSrc.startsWith('http') || displayData.logoSrc.startsWith('/') ? (
                    <img src={displayData.logoSrc} className="h-6 w-auto" alt={displayData.logoAlt} />
                  ) : (
                    <Icon icon={displayData.logoSrc} className="h-6 w-6" />
                  )}
                </>
              )}
              {displayData.title && (
                <span className="text-lg font-semibold whitespace-nowrap" style={{ color: displayData.titleColor || '#231838' }}>
                  {displayData.title}
                </span>
              )}
            </div>
          )}
          
          {displayData.content && (
            <p className="text-sm font-normal" style={{ color: displayData.textColor || '#231838' }}>
              {displayData.content}
            </p>
          )}
          
          {displayData.showButton && displayData.buttonText && (
            <a
              href={displayData.buttonLink}
              onClick={handleBannerClick}
              className="px-5 py-2 text-xs font-medium rounded-lg transition-colors duration-200"
              style={{ 
                color: displayData.buttonTextColor || '#FFFFFF',
                backgroundColor: displayData.buttonColor || '#926fe5'
              }}
            >
              {displayData.buttonText}
            </a>
          )}
        </div>
        
        {/* Close Button - Positioned Absolute Right */}
        {displayData.showCloseButton && (
          <button
            onClick={handleClose}
            type="button"
            className="absolute right-4 inline-flex justify-center w-7 h-7 items-center hover:opacity-75 rounded-lg p-1.5 transition-colors duration-200"
            style={{ color: displayData.textColor || '#FFFFFF' }}
            aria-label="Close banner"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default StickyBanner;